import { Barangay, PollResponse, Voter } from '../../types';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';

interface BarangayVotersTableRowProps {
  idx: number;
  voter: Voter;
}

const BarangayVotersTableRow: React.FC<BarangayVotersTableRowProps> = ({
  idx,
  voter
}) => {
  const dashboardId = 'SURIGAO DEL NORTE_DISTRICT 1';
  const [wasPolled, setWasPolled] = useState<boolean | null>(null);
  const [isFinalReleased, setIsFinalReleased] = useState<boolean | null>(null);
  const [isExitPolled, setIsExitPolled] = useState<boolean | null>(null);
  
  const wasPolledBefore = async (voter: Voter): Promise<boolean> => {
    try {
      const latestPollResponsePath = 
        `tabulationsV2/${dashboardId}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;

      const latestPollResponseRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseRef);
      if (latestPollResponseDoc.exists()) {
        const latestPollResponse = latestPollResponseDoc.data() as PollResponse;
        return latestPollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering';
      }
    } catch (error) {
      console.error('Error checking if voter was polled before: ', error);
    }
    return false;
  };

  const checkIsFinalReleased = async (voter: Voter): Promise<boolean> => {
    // Check final releases
    const finalReleasesQuery = query(
      collection(db, 'finalReleases'), 
      where('voterId', '==', voter.id)
    );
    const finalReleasesSnapshot = await getDocs(finalReleasesQuery);
    return !finalReleasesSnapshot.empty;
  };

  const checkIsExitPolled = async (voter: Voter): Promise<boolean> => {
    // Check exit polls
    const exitPollsQuery = query(
      collection(db, 'exitPolls'), 
      where('voterId', '==', voter.id)
    );
    const exitPollsSnapshot = await getDocs(exitPollsQuery);
    return !exitPollsSnapshot.empty;
  };

  useEffect(() => {
    const fetchVoterStatus = async () => {
      try {
        const [polledStatus, finalReleasedStatus, exitPollStatus] = await Promise.all([
          wasPolledBefore(voter),
          checkIsFinalReleased(voter),
          checkIsExitPolled(voter)
        ]);

        setWasPolled(polledStatus);
        setIsFinalReleased(finalReleasedStatus);
        setIsExitPolled(exitPollStatus);
      } catch (error) {
        console.error('Error fetching voter status:', error);
      }
    };

    fetchVoterStatus();
  }, [voter]);

  return (
    <TableRow key={voter.id}>
      <TableCell>{idx + 1}</TableCell>
      <TableCell>{voter.name}</TableCell>
      <TableCell>{voter.precinct}</TableCell>
      <TableCell>
        {wasPolled === null ? <CircularProgress size={20} /> : (wasPolled ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          ))}
      </TableCell>
      <TableCell>
        {isFinalReleased === null ? <CircularProgress size={20} /> : (isFinalReleased ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          ))}
      </TableCell>
      <TableCell>
        {isExitPolled === null ? <CircularProgress size={20} /> : (isExitPolled ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          ))}
      </TableCell>
    </TableRow>
  );
};

const BarangayVoters: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [voters, setVoters] = useState<Voter[]>([]);
  const [loading, setLoading] = useState(true);
  const [barangayName, setBarangayName] = useState('');
  const [municipality, setMunicipality] = useState('');

  useEffect(() => {
    const fetchVoters = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const queryBarangayName = searchParams.get('barangayName');
        const queryMunicipality = searchParams.get('municipality');

        if (!queryBarangayName || !queryMunicipality || !user) {
          setLoading(false);
          return;
        }

        const brgyName = decodeURIComponent(queryBarangayName);
        const munName = decodeURIComponent(queryMunicipality);

        setBarangayName(brgyName);
        setMunicipality(munName);

        // Fetch voters for this barangay
        const votersRef = collection(db, 'voters');
        const votersQuery = query(
          votersRef, 
          where('barangay', '==', brgyName),
          where('city_municipality', '==', munName)
        );
        const votersSnapshot = await getDocs(votersQuery);

        const fetchedVoters: Voter[] = votersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Voter));

        setVoters(fetchedVoters.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching barangay voters:', error);
        setLoading(false);
      }
    };

    if (user) {
      setLoading(true);
      fetchVoters();      
    }
  }, [location.search, user]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', typography: 'body1', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Voters in {barangayName}, {municipality}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Precinct</TableCell>
              <TableCell>Polled</TableCell>
              <TableCell>Final Released</TableCell>
              <TableCell>Exit Polled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {voters.map((voter, idx) => (
              <BarangayVotersTableRow key={voter.id} idx={idx} voter={voter} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BarangayVoters;
